import { isPlanningStateChangeInScope } from "./planningCrossViewSync.js";

export const SOURCE_CHANGES_NEUTRAL_PLANNING_ACTIONS = new Set([
  "move_delivery",
  "move_delivery_group",
  "segment_reorder",
  "reassign_truck_orders",
  "release",
  "release_delivery_group",
  "suspend",
  "suspend_delivery_group",
  "reactivate",
  "unconfirm",
  "unconfirm_delivery_group",
  "unconfirm_loading",
  "unconfirm_order",
  "unfreeze",
  "unfreeze_delivery_group",
  "unprotect_truck",
  "unprotect_truck_orders",
]);

export const SOURCE_CHANGES_CONTEXT_ONLY_PLANNING_ACTIONS = new Set([
  "move_delivery",
  "move_delivery_group",
  "reassign_truck_orders",
  "release",
  "release_delivery_group",
  "suspend",
  "suspend_delivery_group",
  "reactivate",
  "unconfirm",
  "unconfirm_delivery_group",
  "unconfirm_loading",
  "unconfirm_order",
  "unfreeze",
  "unfreeze_delivery_group",
  "unprotect_truck",
  "unprotect_truck_orders",
]);

export const SOURCE_CHANGES_IRRELEVANT_PLANNING_ACTIONS = new Set([
  "clear_truck_preload_time",
  "create_dispatch_snapshot_save",
  "delete_dispatch_snapshot",
  "publish_driver_portal_routes",
  "reset_here_route_cache",
  "reset_optimization_runtime_cache",
  "set_truck_preload_time",
  "unpublish_driver_portal_routes",
]);

export const SOURCE_CHANGES_PERFORMANCE_CAUSES = new Set([
  "bootstrap",
  "data_manager",
  "evening_rail_open",
  "portail",
  "visibility",
  "manual_open",
  "resume",
  "source_changes_action",
  "movement_verification",
]);

export const SOURCE_CHANGES_PERFORMANCE_ORIGINS = new Set([
  "local",
  "broadcast",
]);

export const SOURCE_CHANGES_PERFORMANCE_CAUSE_HEADER = "X-TransIA-Source-Changes-Cause";
export const PERFORMANCE_ACTION_HEADER = "X-TransIA-Performance-Action";
export const PERFORMANCE_CHAIN_HEADER = "X-TransIA-Performance-Chain";
export const PERFORMANCE_ORIGIN_HEADER = "X-TransIA-Performance-Origin";
export const PERFORMANCE_TAB_HEADER = "X-TransIA-Performance-Tab";

const PERFORMANCE_IDENTIFIER_PATTERN = /^[a-f0-9-]{16,64}$/;
const SOURCE_CHANGES_PERFORMANCE_ACTIONS = new Set([
  "source_changes_acknowledge_protected",
  "source_changes_import_new",
  "source_changes_import_suspendable_errors",
  "source_changes_open",
  "source_changes_recover_truck",
  "source_changes_reject_truck",
  "source_changes_reject_loading_constraints",
  "source_changes_remove_date_changed",
  "source_changes_remove_deleted",
  "source_changes_remove_modified",
  "source_changes_remove_orphan_end_of_day_loads",
  "source_changes_repair_non_renderable_trip",
  "source_changes_resolve_end_of_day_load_integrity",
  "source_changes_restore_executions",
  "source_changes_resume",
  "source_changes_refresh_loading_constraints",
  "source_changes_refresh_trucks",
  "source_changes_suspend_non_renderable_trip",
]);
const SOURCE_CHANGES_AGGREGATE_CACHE_MAX_SCOPES = 8;
const SOURCE_CHANGES_AGGREGATE_CACHE_TTL_MS = 60 * 60 * 1000;

let performanceTabId = "";

function normalizeInlineText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeDispatchPeriod(value) {
  return normalizeInlineText(value).toLowerCase() === "evening" ? "evening" : "day";
}

function randomHex(size) {
  const byteCount = Math.ceil(size / 2);
  const bytes = new Uint8Array(byteCount);
  if (globalThis.crypto?.getRandomValues) {
    globalThis.crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  return Array.from(bytes, (value) => value.toString(16).padStart(2, "0"))
    .join("")
    .slice(0, size);
}

export function createPerformanceCorrelationId() {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  return `${randomHex(8)}-${randomHex(4)}-4${randomHex(3)}-a${randomHex(3)}-${randomHex(12)}`;
}

export function getPerformanceTabId() {
  if (!performanceTabId) {
    performanceTabId = createPerformanceCorrelationId();
  }
  return performanceTabId;
}

export function normalizePerformanceCorrelationId(value) {
  const normalizedValue = normalizeInlineText(value).toLowerCase();
  return PERFORMANCE_IDENTIFIER_PATTERN.test(normalizedValue) ? normalizedValue : "";
}

export function buildSourceChangesPerformanceHeaders({
  action = "",
  cause = "source_changes_action",
  chainId = "",
  origin = "local",
  tabId = "",
} = {}) {
  const safeCause = normalizeInlineText(cause).toLowerCase();
  const safeOrigin = normalizeInlineText(origin).toLowerCase();
  const safeAction = normalizeInlineText(action).toLowerCase();
  const safeChainId = normalizePerformanceCorrelationId(chainId) || createPerformanceCorrelationId();
  const safeTabId = normalizePerformanceCorrelationId(tabId) || getPerformanceTabId();
  return {
    ...(SOURCE_CHANGES_PERFORMANCE_ACTIONS.has(safeAction)
      ? { [PERFORMANCE_ACTION_HEADER]: safeAction }
      : {}),
    [SOURCE_CHANGES_PERFORMANCE_CAUSE_HEADER]: SOURCE_CHANGES_PERFORMANCE_CAUSES.has(safeCause)
      ? safeCause
      : "source_changes_action",
    [PERFORMANCE_CHAIN_HEADER]: safeChainId,
    [PERFORMANCE_ORIGIN_HEADER]: SOURCE_CHANGES_PERFORMANCE_ORIGINS.has(safeOrigin)
      ? safeOrigin
      : "local",
    [PERFORMANCE_TAB_HEADER]: safeTabId,
  };
}

export function buildPlanningPerformanceCorrelationHeaders({ chainId = "", tabId = "" } = {}) {
  const safeChainId = normalizePerformanceCorrelationId(chainId);
  const safeTabId = normalizePerformanceCorrelationId(tabId);
  return {
    ...(safeChainId ? { [PERFORMANCE_CHAIN_HEADER]: safeChainId } : {}),
    ...(safeTabId ? { [PERFORMANCE_TAB_HEADER]: safeTabId } : {}),
  };
}

export function isNeutralSourceChangesPlanningAction(detail) {
  const actionName = normalizeInlineText(detail?.actionName || detail?.action_name).toLowerCase();
  return SOURCE_CHANGES_NEUTRAL_PLANNING_ACTIONS.has(actionName);
}

export function isContextOnlySourceChangesPlanningAction(detail) {
  const actionName = normalizeInlineText(detail?.actionName || detail?.action_name).toLowerCase();
  return SOURCE_CHANGES_CONTEXT_ONLY_PLANNING_ACTIONS.has(actionName);
}

export function isIrrelevantSourceChangesPlanningAction(detail) {
  const actionName = normalizeInlineText(detail?.actionName || detail?.action_name).toLowerCase();
  return SOURCE_CHANGES_IRRELEVANT_PLANNING_ACTIONS.has(actionName);
}

export function classifySourceChangesPlanningEvent(detail, activeScope = {}) {
  if (!isPlanningStateChangeInScope(detail, activeScope)) {
    return { behavior: "ignore", cause: "", origin: "local", chainId: "" };
  }

  const source = normalizeInlineText(detail?.source).toLowerCase();
  const origin = detail?.fromBroadcast === true ? "broadcast" : "local";
  const chainId = normalizePerformanceCorrelationId(
    detail?.performanceChainId || detail?.performance_chain_id,
  );
  if (source === "data-manager") {
    return { behavior: "refresh", cause: "data_manager", origin, chainId };
  }
  if (source === "portail") {
    return { behavior: "defer", cause: "portail", origin, chainId };
  }
  if (source !== "dispatch") {
    return { behavior: "ignore", cause: "", origin, chainId };
  }
  if (isIrrelevantSourceChangesPlanningAction(detail)) {
    return { behavior: "ignore", cause: "", origin, chainId };
  }
  const actionName = normalizeInlineText(detail?.actionName || detail?.action_name).toLowerCase();
  if (actionName === "optimize_truck_routes") {
    return { behavior: "refresh", cause: "source_changes_action", origin, chainId };
  }
  if (actionName === "segment_reorder") {
    const sourceChangesVerificationRequired = detail?.sourceChangesVerificationRequired
      ?? detail?.source_changes_verification_required;
    return {
      behavior: sourceChangesVerificationRequired === false ? "defer_context" : "defer",
      cause: "movement_verification",
      origin,
      chainId,
    };
  }
  if (isContextOnlySourceChangesPlanningAction(detail)) {
    return { behavior: "defer_context", cause: "movement_verification", origin, chainId };
  }
  if (isNeutralSourceChangesPlanningAction(detail)) {
    return { behavior: "defer", cause: "movement_verification", origin, chainId };
  }
  if (origin === "local" && detail?.skipLocalSourceChangesRefresh === true) {
    return { behavior: "ignore", cause: "", origin, chainId };
  }
  if (origin === "broadcast" && detail?.skipLocalSourceChangesRefresh === true) {
    return { behavior: "refresh", cause: "source_changes_action", origin, chainId };
  }
  return { behavior: "defer", cause: "source_changes_action", origin, chainId };
}

export function resolveSourceChangesAggregateCount(sourceChanges = null) {
  const counts = sourceChanges?.counts && typeof sourceChanges.counts === "object"
    ? sourceChanges.counts
    : {};
  const candidate = counts.unresolved_total ?? counts.total;
  // Camions hors limite EXP (catégorie hors counts du backend).
  const compartmentFillDetails = sourceChanges?.compartmentFillViolations?.details;
  const count = Number(candidate) + (Array.isArray(compartmentFillDetails) ? compartmentFillDetails.length : 0);
  if (!Number.isFinite(count)) {
    return null;
  }

  const truckChanges = sourceChanges?.truckChanges;
  if (!truckChanges) {
    return Math.max(0, Math.round(count));
  }
  if (truckChanges.state === "legacy_or_absent" || truckChanges.state === "disabled") {
    return Math.max(0, Math.round(count));
  }
  if (truckChanges.state === "unavailable") {
    return null;
  }
  if (truckChanges.state !== "ready" && truckChanges.state !== "reference_required") {
    return null;
  }
  const truckCandidate = truckChanges?.counts?.unresolved;
  if (!Number.isInteger(truckCandidate) || truckCandidate < 0) {
    return null;
  }
  return Math.max(0, Math.round(count)) + truckCandidate;
}

export function isSourceChangesPreviewFresh(sourceChanges = null, expectedScope = {}) {
  const state = sourceChanges && typeof sourceChanges === "object" ? sourceChanges : {};
  const dispatchDate = normalizeInlineText(expectedScope.dispatchDate);
  const expectedDispatchPeriod = normalizeInlineText(expectedScope.dispatchPeriod);
  const stateDispatchPeriod = normalizeInlineText(state.dispatchPeriod);
  const dispatchPeriod = normalizeDispatchPeriod(expectedDispatchPeriod);
  const previewRevision = normalizeInlineText(
    Object.prototype.hasOwnProperty.call(expectedScope, "previewRevision")
      ? expectedScope.previewRevision
      : state.previewRevision,
  );
  return Boolean(dispatchDate)
    && Boolean(expectedDispatchPeriod)
    && Boolean(stateDispatchPeriod)
    && Boolean(previewRevision)
    && state.hasVerified === true
    && state.needsVerification !== true
    && state.isLoading !== true
    && state.isMutating !== true
    && state.isVerifying !== true
    && !normalizeInlineText(state.errorMessage)
    && normalizeInlineText(state.dispatchDate) === dispatchDate
    && normalizeDispatchPeriod(stateDispatchPeriod) === dispatchPeriod;
}

export function isSourceChangesActionPreviewFresh(sourceChanges = null, expectedScope = {}) {
  return sourceChanges?.needsPlanningContextVerification !== true
    && isSourceChangesPreviewFresh(sourceChanges, expectedScope);
}

export function createSourceChangesAggregateCache({
  maxScopes = SOURCE_CHANGES_AGGREGATE_CACHE_MAX_SCOPES,
  now = () => Date.now(),
  ttlMs = SOURCE_CHANGES_AGGREGATE_CACHE_TTL_MS,
} = {}) {
  const entries = new Map();
  const safeMaxScopes = Math.max(1, Math.floor(Number(maxScopes) || SOURCE_CHANGES_AGGREGATE_CACHE_MAX_SCOPES));
  const safeTtlMs = Math.max(1, Math.floor(Number(ttlMs) || SOURCE_CHANGES_AGGREGATE_CACHE_TTL_MS));

  const buildScopeKey = ({ dispatchDate = "", dispatchPeriod = "day" } = {}) => {
    const safeDispatchDate = normalizeInlineText(dispatchDate);
    return safeDispatchDate ? `${safeDispatchDate}|${normalizeDispatchPeriod(dispatchPeriod)}` : "";
  };
  const prune = () => {
    const currentTime = now();
    for (const [scopeKey, entry] of entries) {
      if (currentTime - entry.verifiedAt >= safeTtlMs) {
        entries.delete(scopeKey);
      }
    }
    while (entries.size > safeMaxScopes) {
      const oldestScopeKey = entries.keys().next().value;
      entries.delete(oldestScopeKey);
    }
  };

  return {
    invalidate(scope = {}) {
      return entries.delete(buildScopeKey(scope));
    },
    read(scope = {}) {
      prune();
      const scopeKey = buildScopeKey(scope);
      return scopeKey && entries.has(scopeKey) ? entries.get(scopeKey).pendingCount : null;
    },
    write(scope = {}, pendingCount = null) {
      const scopeKey = buildScopeKey(scope);
      const safePendingCount = Number(pendingCount);
      if (!scopeKey || !Number.isFinite(safePendingCount)) {
        return false;
      }
      entries.delete(scopeKey);
      entries.set(scopeKey, {
        pendingCount: Math.max(0, Math.round(safePendingCount)),
        verifiedAt: now(),
      });
      prune();
      return true;
    },
  };
}

export const sourceChangesAggregateCache = createSourceChangesAggregateCache();
